import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { MaskedLine } from "../Reveal";
import { SITE, waLink } from "../../data/site";
import { track } from "../../lib/analytics";
import { scrollToHash } from "../../lib/scroll";

const INDICATORS = ["Comunicação Visual", "Material Gráfico", "Personalização", "Soluções para Empresas"];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

    return (
        <section id="inicio" ref={ref} data-testid="hero-section" className="grain relative overflow-hidden bg-ink text-white">
            <div className="hero-grid-bg absolute inset-0" />
            <div className="absolute -top-40 -left-40 h-[480px] w-[480px] rounded-full bg-brand/15 blur-[140px]" />

            <motion.div style={{ opacity: fade }} className="container-x relative pt-32 sm:pt-36 lg:pt-44 pb-14 lg:pb-20">
                <div className="grid items-center gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <motion.p
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.15 }}
                            className="section-tag flex items-center gap-3"
                        >
                            <span className="inline-block h-px w-8 bg-brand" />
                            Gráfica · Comunicação Visual · {SITE.city}
                        </motion.p>

                        <h1 className="mt-6 font-display text-[2.65rem] sm:text-6xl lg:text-[4.6rem] font-extrabold leading-[1.02] tracking-tight">
                            <MaskedLine delay={0.25}>Sua marca precisa</MaskedLine>
                            <MaskedLine delay={0.37} className="text-brand">aparecer.</MaskedLine>
                            <MaskedLine delay={0.49}>A gente coloca ela</MaskedLine>
                            <MaskedLine delay={0.61}>
                                em <span className="text-brand">evidência.</span>
                            </MaskedLine>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.85 }}
                            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-mist"
                        >
                            Materiais gráficos, comunicação visual, personalização e soluções para empresas em
                            Campinas e região.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 1 }}
                            className="mt-9 flex flex-col sm:flex-row gap-4"
                        >
                            <a
                                href={waLink()}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => track("whatsapp_click", { location: "hero" })}
                                data-testid="hero-cta-whatsapp"
                                className="group inline-flex items-center justify-center gap-3 rounded-full bg-brand px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-brand-dark hover:shadow-[0_10px_40px_rgba(255,85,0,0.4)]"
                            >
                                <MessageCircle size={18} fill="currentColor" />
                                Pedir orçamento no WhatsApp
                            </a>
                            <button
                                onClick={() => scrollToHash("#servicos")}
                                data-testid="hero-cta-servicos"
                                className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:border-brand hover:text-brand"
                            >
                                Ver nossos serviços
                                <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:translate-y-0.5" />
                            </button>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ clipPath: "inset(100% 0 0 0)" }}
                            animate={{ clipPath: "inset(0% 0 0 0)" }}
                            transition={{ duration: 1.2, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                            className="relative overflow-hidden rounded-2xl border border-line"
                        >
                            <motion.img
                                style={{ y: imgY, scale: 1.15 }}
                                src="/images/hero.jpg"
                                alt="Produção de comunicação visual e materiais gráficos na IMPRIMA AKI em Campinas"
                                data-testid="hero-image"
                                loading="eager"
                                className="aspect-[4/5] sm:aspect-[5/5] w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.3, duration: 0.6 }}
                                className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-xl border border-white/10 bg-ink/70 px-4 py-3 backdrop-blur-md"
                            >
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/80">
                                    Produção própria
                                </span>
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand">
                                    {SITE.city}
                                </span>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.35, duration: 0.8 }}
                    data-testid="hero-indicators"
                    className="mt-16 lg:mt-20 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-4"
                >
                    {INDICATORS.map((item, i) => (
                        <div key={item} className="flex items-center gap-3 bg-coal px-5 py-4">
                            <span className="font-mono text-[10px] text-brand">0{i + 1}</span>
                            <span className="text-xs sm:text-sm font-semibold text-white/85">{item}</span>
                        </div>
                    ))}
                </motion.div>
            </motion.div>
        </section>
    );
};
